import React from 'react';
import { OrderSummary } from '../../../types';
import { OrderStatusBadge, PaymentStatusBadge, DateDisplay, TableRow } from '../atoms';
import './OrdersTable.css';

interface OrdersTableProps {
  orders: OrderSummary[];
  onOrderClick?: (order: OrderSummary) => void;
  onEdit?: (order: OrderSummary) => void;
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const OrdersTable: React.FC<OrdersTableProps> = ({ 
  orders, 
  onOrderClick, 
  onEdit,
  loading = false,
  error = null,
  className = '' 
}) => {
  const headers = [
    'Order Number',
    'Customer',
    'Status',
    'Payment',
    'Total',
    'Items',
    'Created',
    'Delivery',
    'Actions'
  ];

  const handleRowClick = (order: OrderSummary) => {
    if (onOrderClick) {
      onOrderClick(order);
    }
  };

  const handleEdit = (e: React.MouseEvent, order: OrderSummary) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(order);
    }
  };

  if (loading) {
    return (
      <div className={`orders-table loading ${className}`}>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`orders-table error ${className}`}>
        <div className="error-state">
          <h3>Error Loading Orders</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className={`orders-table empty ${className}`}>
        <div className="empty-state">
          <h3>No Orders Found</h3>
          <p>No orders match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`orders-table ${className}`}>
      <div className="table-container">
        <table className="orders-table-content">
          <thead>
            <tr className="table-header-row">
              {headers.map((header, index) => (
                <th key={index} className="table-header-cell">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <TableRow
                key={order.id}
                onClick={() => handleRowClick(order)}
                className="order-row"
              >
                <td className="order-number">
                  {order.orderNumber}
                </td>
                <td className="customer-info">
                  <div className="customer-name">{order.customerName}</div>
                  <div className="customer-email">{order.customerEmail}</div>
                </td>
                <td className="order-status">
                  <OrderStatusBadge status={order.status} />
                </td>
                <td className="payment-status">
                  <PaymentStatusBadge status={order.paymentStatus} />
                </td>
                <td className="order-total">
                  <strong>€{order.total.toFixed(2)}</strong>
                </td>
                <td className="item-count">
                  {order.itemCount}
                </td>
                <td className="created-date">
                  <DateDisplay date={order.createdAt} format="short" />
                </td>
                <td className="delivery-date">
                  {order.deliveryDate ? (
                    <DateDisplay date={order.deliveryDate} format="short" />
                  ) : (
                    <span className="no-delivery">-</span>
                  )}
                </td>
                <td className="actions-cell">
                  {onEdit && (
                    <button
                      className="edit-order-btn"
                      onClick={(e) => handleEdit(e, order)}
                      title="Edit order"
                      aria-label="Edit order"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.334 2.00004C11.5091 1.82494 11.7169 1.68605 11.9457 1.59129C12.1745 1.49653 12.4197 1.44775 12.6673 1.44775C12.9149 1.44775 13.1601 1.49653 13.3889 1.59129C13.6177 1.68605 13.8256 1.82494 14.0007 2.00004C14.1758 2.17513 14.3147 2.383 14.4094 2.61178C14.5042 2.84055 14.553 3.08575 14.553 3.33337C14.553 3.58099 14.5042 3.82619 14.4094 4.05497C14.3147 4.28374 14.1758 4.49161 14.0007 4.66671L5.00065 13.6667L1.33398 14.6667L2.33398 11L11.334 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </td>
              </TableRow>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTable;
